import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import StatusBadge from "@/components/StatusBadge";
import {
  FileText, ClipboardCheck, Package, CheckCircle2, Bell, Wrench, PlusCircle,
  TrendingUp, Factory, ShieldCheck, Users, BarChart3, ClipboardList, Library, Truck,
} from "lucide-react";

function Stat({ icon: Icon, label, value, hint, color = "text-brand-amber", testId }) {
  return (
    <div className="card-industrial p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className={`w-10 h-10 rounded-md bg-slate-50 flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${color}`} />
        </div>
      </div>
      <div className="text-3xl font-bold text-brand-navy">{value ?? "—"}</div>
      <div className="text-sm text-slate-600 mt-1">{label}</div>
      {hint && <div className="text-xs text-slate-400 mt-1">{hint}</div>}
    </div>
  );
}

export default function DashboardHome() {
  const { user } = useAuth();
  const [summary, setSummary] = useState({});
  const [requests, setRequests] = useState([]);
  const [orders, setOrders] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [inspections, setInspections] = useState([]);

  useEffect(() => {
    (async () => {
      const s = await api.get("/analytics/summary").then((r) => r.data).catch(() => ({}));
      setSummary(s);
      if (user.role === "factory_customer") {
        const [r, o] = await Promise.all([api.get("/requests"), api.get("/orders")]);
        setRequests(r.data.requests || []); setOrders(o.data.orders || []);
      } else if (user.role === "workshop_partner") {
        const [q, o] = await Promise.all([api.get("/rfqs"), api.get("/orders")]);
        setRfqs(q.data.rfqs || []); setOrders(o.data.orders || []);
      } else if (user.role === "inspector") {
        const [i, r] = await Promise.all([api.get("/inspections"), api.get("/requests")]);
        setInspections(i.data.inspections || []); setRequests(r.data.requests || []);
      } else {
        const [r, o] = await Promise.all([api.get("/requests"), api.get("/orders")]);
        setRequests(r.data.requests || []); setOrders(o.data.orders || []);
      }
    })();
  }, [user.role]);

  return (
    <DashboardLayout>
      <div className="mb-6">
        <div className="text-xs font-bold tracking-widest text-brand-amber mb-2">لوحة التحكم</div>
        <h1 className="text-3xl font-bold text-brand-navy">مرحباً، {user?.name} 👋</h1>
        <p className="text-slate-600 mt-1">إليك ملخصاً بأحدث نشاطك على منصة MERQAV.</p>
      </div>

      {/* Email verification banner + profile completion widget */}
      {(user.role === "factory_customer" || user.role === "workshop_partner") && (
        <div className="grid gap-3 md:grid-cols-2 mb-6" data-testid="onboarding-banners">
          {!user.email_verified && (
            <div className="card-industrial p-4 border-amber-300 bg-amber-50" data-testid="unverified-email-banner">
              <div className="text-sm font-semibold text-amber-900 mb-1">فعّل بريدك الإلكتروني</div>
              <p className="text-xs text-amber-800 mb-2">لم يتم تفعيل بريدك بعد. سيؤثر ذلك على مستوى الثقة.</p>
              <Button size="sm" variant="outline" className="border-amber-400 text-amber-800" data-testid="resend-verification-btn"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/auth/resend-verification");
                    if (data.already_verified) window.location.reload();
                    else if (data.verify_path) window.location.href = data.verify_path;
                  } catch (_err) { /* ignore resend errors */ }
                }}>
                إرسال رابط تفعيل جديد
              </Button>
            </div>
          )}
          {user.profile_completion !== undefined && user.profile_completion < 100 && (
            <div className="card-industrial p-4" data-testid="profile-completion-widget">
              <div className="flex items-center justify-between mb-2">
                <div className="text-sm font-semibold text-brand-navy">اكتمال الملف</div>
                <span className="text-lg font-bold text-brand-amber">{user.profile_completion}%</span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div className="h-full bg-brand-amber transition-all" style={{ width: `${user.profile_completion}%` }} />
              </div>
              <p className="text-xs text-slate-500 mt-2">أكمل ملفك لرفع فرصك في اعتماد الطلبات وسرعة المعالجة.</p>
            </div>
          )}
        </div>
      )}

      {/* Stats by role */}
      {user.role === "factory_customer" && (
        <>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
            <Stat icon={FileText} label="إجمالي الطلبات" value={summary.total_requests} testId="stat-total-requests" />
            <Stat icon={ClipboardCheck} label="بانتظار موافقتك" value={summary.awaiting_approval} testId="stat-awaiting-approval" />
            <Stat icon={Package} label="طلبيات نشطة" value={summary.active_orders} testId="stat-active-orders" />
            <Stat icon={CheckCircle2} label="طلبيات مكتملة" value={summary.completed_orders} color="text-emerald-600" testId="stat-completed" />
          </div>
          <div className="flex flex-wrap gap-2 mb-8">
            <Link to="/app/new-request"><Button className="btn-cta" data-testid="quick-new-request-btn"><PlusCircle className="w-4 h-4 me-2" /> طلب تصنيع قطعة</Button></Link>
            <Link to="/app/part-library"><Button variant="outline" data-testid="quick-part-library-btn"><Library className="w-4 h-4 me-2" /> مكتبة القطع</Button></Link>
            <Link to="/app/quotations"><Button variant="outline" data-testid="quick-quotations-btn"><ClipboardCheck className="w-4 h-4 me-2" /> عروض الأسعار</Button></Link>
          </div>
        </>
      )}

      {user.role === "workshop_partner" && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
          <Stat icon={ClipboardList} label="طلبات عروض جديدة" value={summary.new_rfqs} testId="stat-new-rfqs" />
          <Stat icon={FileText} label="عروض مقدمة" value={summary.quotations_submitted} testId="stat-quotations" />
          <Stat icon={Wrench} label="أعمال نشطة" value={summary.active_jobs} color="text-purple-600" testId="stat-active-jobs" />
          <Stat icon={CheckCircle2} label="أعمال مكتملة" value={summary.completed_jobs} color="text-emerald-600" testId="stat-completed-jobs" />
        </div>
      )}

      {user.role === "inspector" && (
        <div className="grid gap-4 md:grid-cols-3 mb-8">
          <Stat icon={ClipboardCheck} label="زيارات مسندة" value={summary.assigned_visits} testId="stat-assigned-visits" />
          <Stat icon={FileText} label="تقارير الفحص" value={summary.inspection_reports} testId="stat-inspection-reports" />
          <Stat icon={ShieldCheck} label="تقارير الجودة" value={summary.quality_reports} color="text-emerald-600" testId="stat-quality-reports" />
        </div>
      )}

      {(user.role === "admin" || user.role === "operations_employee") && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
          <Stat icon={FileText} label="إجمالي الطلبات" value={summary.total_requests} testId="stat-total-requests" />
          <Stat icon={Package} label="طلبيات نشطة" value={summary.active_orders} testId="stat-active-orders" />
          <Stat icon={Wrench} label="ورش معتمدة" value={summary.approved_workshops} color="text-emerald-600" testId="stat-workshops" />
          <Stat icon={Factory} label="مصانع" value={summary.total_factories} testId="stat-factories" />
          {user.role === "admin" && (
            <>
              <Stat icon={TrendingUp} label="إجمالي المبيعات (ر.س)" value={summary.total_revenue?.toLocaleString?.()} color="text-emerald-600" testId="stat-revenue" />
              <Stat icon={BarChart3} label="الربح الإجمالي (ر.س)" value={summary.gross_profit?.toLocaleString?.()} color="text-brand-amber" testId="stat-profit" />
              <Stat icon={Wrench} label="ورش بانتظار الاعتماد" value={summary.pending_workshops} color="text-amber-600" testId="stat-pending-workshops" />
              <Stat icon={CheckCircle2} label="طلبيات مكتملة" value={summary.completed_orders} testId="stat-completed" />
            </>
          )}
        </div>
      )}

      {/* Recent requests table for factory/admin/ops */}
      {(user.role === "factory_customer" || user.role === "admin" || user.role === "operations_employee") && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-6" data-testid="recent-requests-table">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-semibold text-brand-navy">أحدث الطلبات</h3>
            <Link to="/app/requests" className="text-sm text-brand-amber hover:underline">عرض الكل</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  <th className="px-4 py-3 text-start">رقم الطلب</th>
                  <th className="px-4 py-3 text-start">القطعة</th>
                  {user.role !== "factory_customer" && <th className="px-4 py-3 text-start">المصنع</th>}
                  <th className="px-4 py-3 text-start">الكمية</th>
                  <th className="px-4 py-3 text-start">الحالة</th>
                  <th className="px-4 py-3 text-start"></th>
                </tr>
              </thead>
              <tbody>
                {requests.slice(0, 5).map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-semibold text-brand-navy">{r.request_number}</td>
                    <td className="px-4 py-3">{r.part_name}</td>
                    {user.role !== "factory_customer" && <td className="px-4 py-3 text-slate-600">{r.factory_name}</td>}
                    <td className="px-4 py-3">{r.quantity}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3 text-end"><Link to={`/app/requests/${r.id}`} className="text-brand-amber font-semibold hover:underline" data-testid={`view-request-${r.request_number}`}>عرض</Link></td>
                  </tr>
                ))}
                {requests.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-500">لا توجد طلبات بعد.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {user.role === "workshop_partner" && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="recent-rfqs-table">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-semibold text-brand-navy">طلبات عروض الأسعار</h3>
            <Link to="/app/rfqs" className="text-sm text-brand-amber hover:underline">عرض الكل</Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  <th className="px-4 py-3 text-start">رقم RFQ</th>
                  <th className="px-4 py-3 text-start">القطعة</th>
                  <th className="px-4 py-3 text-start">الكمية</th>
                  <th className="px-4 py-3 text-start">الحالة</th>
                  <th className="px-4 py-3 text-start"></th>
                </tr>
              </thead>
              <tbody>
                {rfqs.slice(0, 5).map((r) => (
                  <tr key={r.id} className="border-t border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-3 font-semibold text-brand-navy">{r.rfq_number}</td>
                    <td className="px-4 py-3">{r.part_name}</td>
                    <td className="px-4 py-3">{r.quantity}</td>
                    <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                    <td className="px-4 py-3 text-end"><Link to={`/app/rfqs/${r.id}`} className="text-brand-amber font-semibold" data-testid={`view-rfq-${r.rfq_number}`}>عرض</Link></td>
                  </tr>
                ))}
                {rfqs.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-500">لا توجد طلبات عروض بعد.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {user.role === "inspector" && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="assigned-inspections">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-semibold text-brand-navy">تقارير الفحص</h3>
            <Link to="/app/inspections" className="text-sm text-brand-amber hover:underline">عرض الكل</Link>
          </div>
          <div className="p-6 grid gap-3">
            {inspections.slice(0, 6).map((i) => (
              <div key={i.id} className="border border-slate-200 rounded-md p-4 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-brand-navy">{i.report_number}</div>
                  <div className="text-xs text-slate-500">{i.inspection_date?.slice(0, 10)} • {i.inspection_type}</div>
                </div>
                <StatusBadge status={i.status} />
              </div>
            ))}
            {inspections.length === 0 && <div className="text-center text-slate-500 py-8">لا توجد زيارات مسندة.</div>}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
